import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { ReactNode } from 'react';
import Footer from './Footer';
import PermanentSidebar from './Sidebar/PermanentSidebar';
import Sidebar from './Sidebar/Sidebar';
import Topbar from './Topbar/Topbar';

interface LayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
}

const Layout = ({ children, showSidebar = true }: LayoutProps) => {
  const theme = useTheme();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 38,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Full-width Top Bar */}
      <AppBar
        position='fixed'
        sx={{
          top: 0,
          width: '100%',
          backgroundColor: alpha('#1F1B56', 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: trigger ? theme.shadows[4] : 'none',
          transition: 'all 0.3s ease-in-out',
          zIndex: theme.zIndex.drawer + 1,
        }}
        elevation={trigger ? 1 : 0}
      >
        <Topbar />
      </AppBar>

      {/* Main Layout - Sidebar + Content */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          mt: 8, // Always add top margin for AppBar
        }}
      >
        {/* Sidebar - Permanent on large screens, temporary on small screens */}
        {showSidebar && (
          <>
            <PermanentSidebar />
            <Sidebar />
          </>
        )}

        {/* Main Content */}
        <Box
          component='main'
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'alternate.main',
            px: 3,
            pb: 3,
            pt: 3,
            minHeight: 'calc(100vh - 64px)',
            width: '100%',
            overflow: 'auto',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flexGrow: 1,
              bgcolor: 'transparent',
              width: '100%',
            }}
          >
            {children}
          </Paper>
        </Box>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Layout;
